import axios from "axios";
import { useEffect, useState } from "react";

import { MAGNOLIA_BASE_URL } from "@/constants";

/**
 * Generic hook for fetching Magnolia CMS modal content
 * Used by renewal warning/confirmation modals
 *
 * @param endpoint - The CMS endpoint path (e.g., '/renewal-unsaved-modal')
 * @param initialData - Default/fallback data if fetch fails
 * @param errorMessage - Custom error message for logging
 * @returns { data, loading, error } - Standard fetch hook return pattern
 *
 * @example
 * ```typescript
 * const { data, loading, error } = useFetchMagnoliaModal(
 *   '/renewal-unsaved-modal',
 *   { modalTitle: 'Default', ctaButtonLabel: 'OK' },
 *   'Failed to fetch modal data'
 * );
 * ```
 */
export function useFetchMagnoliaModal<T>(
  endpoint: string,
  initialData: T,
  errorMessage: string
) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      const url = `${MAGNOLIA_BASE_URL}/.rest/delivery/quoteDashBoards${endpoint}`;
      try {
        setLoading(true);
        const response = await axios.get(url, {
          signal: abortController.signal,
        });
        setData(response.data as T);
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error(errorMessage));
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [endpoint, errorMessage]);

  return { data, loading, error };
}
